import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {RatingsMassUpdate} from '../../model/ratings-mass-update';
import {UnitConversionUtils} from '../../configurator/unit-conversion-utils';
import {StringUtils} from '../../utils/string-utils';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { ScheduleLinePerformanceCoilInputComponent } from '../../schedules/schedule-line-performance-coil-input/schedule-line-performance-coil-input.component';

import { FormsModule } from '@angular/forms';

@Component({
    selector: 'app-ratings-mass-update-dialog',
    templateUrl: './ratings-mass-update-dialog.component.html',
    styleUrls: ['./ratings-mass-update-dialog.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, ScheduleLinePerformanceCoilInputComponent, MatButton, MatIcon]
})
export class RatingsMassUpdateDialogComponent {
  private dialogRef = inject<MatDialogRef<RatingsMassUpdateDialogComponent>>(MatDialogRef);
  private dialog = inject(MatDialog);
  private data = inject(MAT_DIALOG_DATA);


  updateData: RatingsMassUpdate;
  metricInput: string;

  constructor() {
    const data = this.data;

    this.updateData = data.updateData;
    this.metricInput = this.updateData.isMetric ? 'True' : 'False';
  }

  metricInputChanged(): void {
    this.updateData.isMetric = 'True' === this.metricInput;
    this.updateData.altitude = this.convertAltitude();
    this.updateData.externalStaticPressure = this.convertEsp();
  }

  private convertEsp(): number {
    return StringUtils.isEmptyNumericValue(this.updateData.externalStaticPressure)
      ? undefined! : this.round(UnitConversionUtils.convertStaticPressure(this.updateData.externalStaticPressure, this.updateData.isMetric), 3);
  }

  private round(value: string, digits: number): number {
    const numericValue = +value;
    const roundedValue = numericValue.toFixed(digits);
    return +roundedValue;
  }

  private convertAltitude(): number {
    return StringUtils.isEmptyNumericValue(this.updateData.altitude)
      ? undefined! : this.round(UnitConversionUtils.convertAltitudeValue(this.updateData.altitude, this.updateData.isMetric), 3);
  }

  getEspUnits(): string {
    return UnitConversionUtils.getEspUnits(this.updateData.isMetric);
  }

  getLengthUnits(): string {
    return UnitConversionUtils.getLengthUnits(this.updateData.isMetric);
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  save(): void {
    this.dialogRef.close(this.updateData);
  }

  getHydronicCoolingInputsLabel(): string {
    const unitsLabel = this.getUnitsLabel(this.updateData.hydronicCoolingLines.length);
    return 'Hydronic Cooling Inputs ' + unitsLabel;
  }

  private getUnitsLabel(collectionSize: number): string {
    return collectionSize === 1 ? '(1 unit)' : '(' + collectionSize + ' units)';
  }

  getRefrigerantInputsLabel(): string {
    const unitsLabel = this.getUnitsLabel(this.updateData.refrigerantLines.length);
    return 'Refrigerant Inputs ' + unitsLabel;
  }

  getHydronicHeatingInputsLabel(): string {
    const unitsLabel = this.getUnitsLabel(this.updateData.hydronicHeatingLines.length);
    return 'Hydronic Heating Inputs ' + unitsLabel;
  }

  getSteamInputsLabel(): string {
    const unitsLabel = this.getUnitsLabel(this.updateData.steamLines.length);
    return 'Steam Inputs ' + unitsLabel;
  }

  getElectricHeatInputsLabel(): string {
    const unitsLabel = this.getUnitsLabel(this.updateData.electricHeatLines.length);
    return 'Electric Heat Inputs ' + unitsLabel;
  }

}
